import { useGetPersonTeamSummary } from '@hr/api/endpoints/index.js'
//import { AlertDismissable } from '@hr/common/AlertDismissable.js'
import { getGenderMap } from '@hr/common/person/personmodalfieldparams.js'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { addGridData, removeGridData, resetSorting, updateGridData } from '@/actions/datagrid.js'
import { getPerson } from '@/actions/persons/personget.js'
import { getAllPersons } from '@/actions/persons/personsgetall.js'
import { groupsGetAllQuery } from '@/api/index.js'
//import { getAllPersonsModified } from '@/actions/persons/personsgetallmodified.js'
import AgGridCommonList from '@/common/ag-grid/AgGridCommonList.jsx'
import { LoadingSuspense } from '@/common/loaders/loadingsuspense.jsx'
import { PdfReport } from '@/common/pdfreport.jsx'
import Switch from '@/common/Switch.jsx'
import { fetchSettings } from '@/common/settings.js'
import { getCountryOptions, selectMapper /*useInterval*/ } from '@/common/utils.jsx'
import { useBusinessIdMap } from '@/hooks/useBusinessIdMap.js'
import {
	actionTypeSelector,
	authSelector,
	personSelector,
	personsModifiedSelector,
	personsSelector,
} from '@/reducers/index.js'
import {
	applyEmployeesFilter,
	applyExternalFilter,
	getEmployeesContextMenuItems as buildEmployeesContextMenuItems,
	getExternalContextMenuItems as buildExternalContextMenuItems,
	createPersonnelListGridProps,
	employeesViewStateKey,
	externalViewStateKey,
	getGridComponents,
} from './agConfigPersonnelList.jsx'
import ExternalWorkforceModal from './modals/external-workforce/index.jsx'
import PersonModal from './modals/person/index.jsx'
import PersonnelTabs from './PersonnelTabs.jsx'
import { getColumns } from './personnelListAgColumns.jsx'

const EXTERNAL_WORKFORCE_MODAL_ENABLED = true

const EXTERNAL_STATUSES = new Set(['NOT_EMPLOYED', 'ENDED_EXTERNAL', 'LONG_TERM_ABSENCE_EXTERNAL'])

const isExternalWorkforce = (person) => {
	return EXTERNAL_STATUSES.has(person?.employmentDetails?.personStatus)
}

const splitByWorkforceType = (list) => {
	const all = structuredClone(list) ?? [] // clone persons to allow mutating personStatus field
	return {
		external: all.filter(isExternalWorkforce),
		employees: all.filter((p) => !isExternalWorkforce(p)),
	}
}

export const PersonnelView = ({ personId, personData }) => {
	const { t, i18n } = useTranslation()
	const dispatch = useDispatch()

	const [tk10, setTk10] = useState([])
	const [cbaCodes, setCbaCodes] = useState([])
	const [countryOptions, setCountryOptions] = useState([])
	const [settings, setSettings] = useState({})
	const [showEnded, setShowEnded] = useState(false)
	const [showEndedExternal, setShowEndedExternal] = useState(false)
	const [viewTab, setViewTab] = useState('employees')

	const [openModalAfterFetch, setOpenModalAfterFetch] = useState(false)
	const [modalShow, setModalShow] = useState(false)
	const [report, setReport] = useState(null)
	/*
	const [alertStatus, setAlertStatus] = useState({
		visible: true,
		severity: 'warning',
		title: 'Uusi toiminnallisuus Henkilöstö-näkymässä',
		message: (
			<>
				<p>Työsuhteiset henkilöt ja ulkopuoliset toimijat näkyvät jatkossa selkeämmin omilla välilehdillään.</p>
				Henkilö tunnistetaan työsuhteiseksi, kun hänelle on lisätty <b>työsuhteen alkupäivä</b> tai <b>palkkatieto</b>.
				Jos henkilö näkyy virheellisesti ulkopuolisissa, muuta status taulukon <b>Status</b>-sarakkeen pudotusvalikosta
				ja täydennä puuttuvat tiedot.
			</>
		),
	})
*/
	//const [modifiedSince, setModifiedSince] = useState(() => Date.now());

	const gridRef = useRef(null)
	const externalGridRef = useRef(null)

	const actionType = useSelector(actionTypeSelector)
	const { hrLevel, companyName, eSalaryCompanyId, supplements } = useSelector(authSelector)
	const person = useSelector(personSelector)
	const persons = useSelector(personsSelector)
	const personsModified = useSelector(personsModifiedSelector)

	const isBasicLevel = hrLevel !== 'premium'
	const { data: businessIdToCompanyNameMap } = useBusinessIdMap()

	const { employees, external } = useMemo(() => splitByWorkforceType(persons), [persons])

	const currentView = viewTab

	const genderMap = getGenderMap(t)

	const { data: personTeams } = useGetPersonTeamSummary()
	const personIdToTeamsMap = useMemo(() => {
		const map = {}
		if (personTeams) {
			personTeams.forEach((pt) => {
				map[pt.personId] = pt.assignedTeams
			})
		}
		return map
	}, [personTeams])

	const { data: groupsData } = useQuery(groupsGetAllQuery)
	const groupsMap = useMemo(() => {
		if (!groupsData) return {}
		return Object.fromEntries(groupsData.map((g) => [g.id, g.name]))
	}, [groupsData])

	const viewColumns = useMemo(
		() =>
			getColumns(currentView, {
				t,
				settings,
				tk10,
				cbaCodes,
				countryOptions,
				businessIdToCompanyNameMap,
				supplements,
				genderMap,
				personIdToTeamsMap,
				groupsMap,
			}),
		[
			currentView,
			t,
			settings,
			tk10,
			cbaCodes,
			countryOptions,
			businessIdToCompanyNameMap,
			supplements,
			genderMap,
			personIdToTeamsMap,
			groupsMap,
		],
	)

	const closeReport = () => setReport(null)

	const fetchPersons = useCallback(() => dispatch(getAllPersons()), [dispatch])
	//const fetchModifiedPersons = useCallback((modifiedAt) => dispatch(getAllPersonsModified(modifiedAt)), [dispatch])

	const modifyPerson = useCallback(
		(selectedPersonId) => {
			if (!Number.isNaN(selectedPersonId)) {
				dispatch(getPerson(selectedPersonId))
			}
		},
		[dispatch],
	)

	const fetchNamespaces = useCallback(async () => {
		await i18n.loadNamespaces(['cbacodes', 'tk10'])
		setCbaCodes(Object.entries(i18n.getResourceBundle(i18n.resolvedLanguage, 'cbacodes') ?? {}).map(selectMapper))
		setTk10(Object.entries(i18n.getResourceBundle(i18n.resolvedLanguage, 'tk10') ?? {}).map(selectMapper))
	}, [i18n])

	useEffect(() => {
		;(async () => {
			setSettings(await fetchSettings())
		})()
		getCountryOptions(setCountryOptions)
		fetchNamespaces()
		return () =>
			dispatch({
				type: 'PERSONS_UNMOUNT',
				payload: { person: [], persons: null },
			})
	}, [fetchNamespaces, dispatch])

	useEffect(() => {
		if (gridRef.current?.api && personData) {
			addGridData(gridRef, [personData], 0)
		}
	}, [personData])

	/*
  useInterval(() => {
    const tmpModifiedSince = modifiedSince;
    setModifiedSince(Date.now());
    fetchModifiedPersons(tmpModifiedSince);
  }, 30 * 1000);
*/

	const employeesGridReady = useCallback(
		({ api }) => {
			//setShowEnded(false)
			applyEmployeesFilter(api, false)
			if (personId) {
				if (personData) {
					addGridData({ current: { api } }, [personData], 0)
				} else {
					dispatch(getPerson(personId))
				}
			} else {
				fetchPersons()
			}
		},
		[personId, personData, dispatch, fetchPersons],
	)

	const externalGridReady = useCallback(
		({ api }) => {
			//setShowEndedExternal(false)
			applyExternalFilter(api, false)
			if (!personId && !persons) {
				fetchPersons()
			}
		},
		[personId, persons, fetchPersons],
	)

	const showEndedEmployments = () => {
		setShowEnded((prev) => !prev)
	}

	useEffect(() => {
		if (!gridRef.current?.api || !persons) return
		applyEmployeesFilter(gridRef.current.api, showEnded)
	}, [persons, showEnded])

	const showEndedExternalEmployments = () => {
		setShowEndedExternal((prev) => !prev)
	}

	useEffect(() => {
		if (!externalGridRef.current?.api || !persons) return
		applyExternalFilter(externalGridRef.current.api, showEndedExternal)
	}, [persons, showEndedExternal])

	useEffect(() => {
		const employeesApiReady = gridRef.current?.api
		const externalApiReady = externalGridRef.current?.api
		if (!employeesApiReady && !externalApiReady) return

		if (personsModified?.newPersons?.length > 0) {
			const split = splitByWorkforceType(personsModified.newPersons)
			if (split.employees.length > 0 && gridRef.current?.api) addGridData(gridRef, split.employees, 0)
			if (split.external.length > 0 && externalGridRef.current?.api) addGridData(externalGridRef, split.external, 0)
		}

		if (personsModified?.modifiedPersons?.length > 0) {
			const split = splitByWorkforceType(personsModified.modifiedPersons)
			if (split.employees.length > 0 && gridRef.current?.api) updateGridData(gridRef, split.employees)
			if (split.external.length > 0 && externalGridRef.current?.api) updateGridData(externalGridRef, split.external)
		}

		if (personsModified?.deletedPersons?.length > 0) {
			if (gridRef.current?.api) removeGridData(gridRef, personsModified.deletedPersons)
			if (externalGridRef.current?.api) removeGridData(externalGridRef, personsModified.deletedPersons)
		}
	}, [personsModified])

	useEffect(() => {
		if (!gridRef.current?.api && !externalGridRef.current?.api) return

		if (actionType === 'POST_PERSON_SUCCEEDED') {
			if (!isExternalWorkforce(person) && gridRef.current?.api) {
				resetSorting(gridRef)
				addGridData(gridRef, [person], 0)
			}
			if (isExternalWorkforce(person) && externalGridRef.current?.api) {
				addGridData(externalGridRef, [person], 0)
			}
			if (isBasicLevel) setModalShow(false)
		} else if (actionType === 'POST_PERSON_EDIT_SUCCEEDED') {
			if (!isExternalWorkforce(person) && gridRef.current?.api) updateGridData(gridRef, [person])
			if (isExternalWorkforce(person) && externalGridRef.current?.api) updateGridData(externalGridRef, [person])
			if (isBasicLevel) setModalShow(false)
		} else if (actionType === 'UPLOAD_DONE') {
			if (!personId) fetchPersons()
			setModalShow(false)
		} else if (actionType === 'GET_PERSON_SUCCEEDED') {
			if (person) {
				if (isExternalWorkforce(person) && externalGridRef.current?.api) addGridData(externalGridRef, [person], 0)
				if (!isExternalWorkforce(person) && gridRef.current?.api) addGridData(gridRef, [person], 0)
			}
			if (openModalAfterFetch) {
				setModalShow(true)
				setOpenModalAfterFetch(false)
			}
		} else if (actionType === 'DELETE_PERSON_SUCCEEDED') {
			if (gridRef.current?.api) removeGridData(gridRef, [person])
			if (externalGridRef.current?.api) removeGridData(externalGridRef, [person])
		}
	}, [actionType, openModalAfterFetch, person, isBasicLevel, personId, fetchPersons])

	const gridComponents = useMemo(() => getGridComponents(eSalaryCompanyId), [eSalaryCompanyId])

	const getContextMenuItems = useCallback(
		(params) =>
			buildEmployeesContextMenuItems({
				t,
				params,
				setReport,
				modifyPerson,
				companyName,
			}),
		[t, modifyPerson, companyName],
	)

	const getExternalContextMenuItems = useCallback(
		(params) =>
			buildExternalContextMenuItems({
				t,
				params,
				setReport,
				modifyPerson,
				companyName,
			}),
		[t, modifyPerson, companyName],
	)

	const handleDoubleClick = useCallback(
		(row) => {
			const selectedId = row.data?.personId
			if (!Number.isNaN(selectedId)) {
				setOpenModalAfterFetch(true)
				modifyPerson(selectedId)
			}
		},
		[modifyPerson],
	)

	const viewConfigs = useMemo(
		() => ({
			employees: {
				rowData: employees,
				columnDefs: currentView === 'employees' ? viewColumns : [],
				getContextMenuItems,
				onGridReady: employeesGridReady,
				initialState: {},
				viewStateKey: employeesViewStateKey,
				highlightEndedRows: true,
			},
			'external-workforce': {
				rowData: external,
				columnDefs: currentView === 'external-workforce' ? viewColumns : [],
				getContextMenuItems: getExternalContextMenuItems,
				onGridReady: externalGridReady,
				initialState: {},
				viewStateKey: externalViewStateKey,
				highlightEndedRows: true,
			},
		}),
		[
			currentView,
			employees,
			external,
			viewColumns,
			getContextMenuItems,
			getExternalContextMenuItems,
			employeesGridReady,
			externalGridReady,
		],
	)

	const ActiveModal =
		currentView === 'external-workforce' && EXTERNAL_WORKFORCE_MODAL_ENABLED ? ExternalWorkforceModal : PersonModal

	const selectedViewConfig = viewConfigs[viewTab] ?? viewConfigs.employees
	const selectedGridRef = viewTab === 'external-workforce' ? externalGridRef : gridRef

	const rowClassRules = useMemo(() => {
		if (!selectedViewConfig?.highlightEndedRows) return undefined
		if (viewTab === 'external-workforce') {
			return {
				endedRow: ({ data }) => data?.employmentDetails?.personStatus === 'ENDED_EXTERNAL',
			}
		}
		return {
			endedRow: ({ data }) => data?.employmentDetails?.personStatus === 'ENDED',
		}
	}, [selectedViewConfig?.highlightEndedRows, viewTab])

	const personnelListGridProps = useMemo(() => createPersonnelListGridProps(dispatch), [dispatch])

	return (
		<>
			{report !== null && <PdfReport json={report} onCloseReport={closeReport} />}
			{actionType === 'GET_PERSON_LOADING' && <LoadingSuspense />}
			{/*alertStatus.visible && <AlertDismissable status={alertStatus} setStatus={setAlertStatus} />*/}

			<Row className="my-1">
				<Col sm="4">
					{!personId && (
						<>
							<h2 className="viewheading">{t('Personnel', 'Henkilöstö')}</h2>
							{viewTab === 'employees' && (
								<Switch
									className="mb-3"
									label={t('ShowEndedEmployments', 'Näytä päättyneet')}
									checked={showEnded}
									onChange={showEndedEmployments}
								/>
							)}
							{viewTab === 'external-workforce' && (
								<Switch
									className="mb-3"
									label={t('ShowEndedEmployments', 'Näytä päättyneet')}
									checked={showEndedExternal}
									onChange={showEndedExternalEmployments}
								/>
							)}
						</>
					)}
				</Col>
				<Col sm="8" align="right">
					<Row className="g-2">
						<Col />
						<Col md="auto">
							<ActiveModal show={modalShow} setShow={setModalShow} personData={person} />
						</Col>
					</Row>
				</Col>
			</Row>

			<PersonnelTabs t={t} activeKey={viewTab} onSelect={(eventKey) => setViewTab(eventKey || 'employees')} />

			{selectedViewConfig && (
				<AgGridCommonList
					key={viewTab}
					gridRef={selectedGridRef}
					rowData={selectedViewConfig.rowData}
					columns={selectedViewConfig.columnDefs}
					components={gridComponents}
					getContextMenuItems={selectedViewConfig.getContextMenuItems}
					onRowDoubleClicked={handleDoubleClick}
					onGridReady={selectedViewConfig.onGridReady}
					getRowId={(params) => String(params.data.personId)}
					initialState={selectedViewConfig.initialState}
					viewStateKey={selectedViewConfig.viewStateKey}
					rowClassRules={rowClassRules}
					agGridProps={personnelListGridProps}
				/>
			)}
		</>
	)
}

export default PersonnelView
